import { useCallback } from 'react'
import { useCreateAdvancedPatientFileMutation } from './createAdvancedPatientFile.generated'

export const MAX_PATIENT_FILE_BYTES = 20 * 1024 * 1024
export const ALLOWED_PATIENT_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf']

export type UploadedPatientFile = { fileID: string; filename: string }

// Two-step: the file goes straight to storage over the signed URL, never through `server`, and only
// the returned `fileID` is attached to the history entry.
export const useUploadPatientFile = (uid: string) => {
  const [createFile] = useCreateAdvancedPatientFileMutation()

  const upload = useCallback(
    async (file: File): Promise<UploadedPatientFile> => {
      if (file.size > MAX_PATIENT_FILE_BYTES) {
        throw new Error(`${file.name} is larger than 20MB.`)
      }
      if (!ALLOWED_PATIENT_FILE_TYPES.includes(file.type)) {
        throw new Error(`${file.name} isn't a supported file type — use jpeg, png or pdf.`)
      }

      const { data } = await createFile({
        variables: { uid, input: { filename: file.name, contentType: file.type, filesize: file.size } },
      })
      if (!data) {
        throw new Error(`Failed to start the upload for ${file.name}.`)
      }

      const { uploadURL, method, file: created } = data.createPatientFile
      const response = await fetch(uploadURL, {
        method,
        headers: { 'Content-Type': file.type, 'x-goog-meta-filename': encodeURIComponent(file.name) },
        body: file,
      })
      if (!response.ok) {
        throw new Error(
          `The upload service rejected “${file.name}” (error ${response.status}). Select the file again to retry. If you need to flag this, please include this error code.`
        )
      }

      return { fileID: created.fileID, filename: created.filename }
    },
    [createFile, uid]
  )

  return upload
}
